import { useEffect, useState } from 'react';

import { usePaginatedAgentByConfigReference } from '@atlassian/conversation-assistant-agent/controllers/agents';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

/**
 * `external_config_reference` of the OOTB Content Reviewer agent that owns the
 * add-suggestions-to-content behaviour.
 *
 * Kept in sync with the platform source of truth,
 * `platform/packages/editor/editor-plugin-ai-suggestions/src/ui/utils/api.ts`
 * (`CONTENT_REVIEWER_AGENT_KEY`), which is internal to that plugin and therefore
 * not importable from Confluence packages.
 */
export const CONTENT_REVIEWER_AGENT_KEY = 'content_reviewer_agent';

/** Identifies this touchpoint to the assistance service for the agent lookup. */
const TOUCHPOINT_SOURCE = 'confluence-edit-page-rovo-chat';

/**
 * Resolves the OOTB Content Reviewer agent for the Rovo Chat empty-state "Review with
 * suggestions" entry point.
 *
 * Pure existence check — it does NOT provision the agent; an unprovisioned agent resolves to
 * `undefined` and the caller hides the entry point. Consumers re-render when the agent resolves.
 */
export const useContentReviewerAgent = (): Agent | undefined => {
	const { cloudId } = useSessionData();
	const { getAndStoreAgentByExternalConfigReference } = usePaginatedAgentByConfigReference({
		assistanceServiceParams: {
			experienceId: 'ai-mate',
			product: 'confluence',
			siteId: cloudId,
			touchpointSource: TOUCHPOINT_SOURCE,
		},
	});

	const [agent, setAgent] = useState<Agent | undefined>(undefined);

	// Resolver returns undefined (never rejects) on failure; depend on it to reflect the latest config.
	useEffect(() => {
		if (!fg('content_reviewer_agent')) {
			return;
		}

		void getAndStoreAgentByExternalConfigReference({
			externalConfigReference: CONTENT_REVIEWER_AGENT_KEY,
		}).then((resolvedAgent) => {
			setAgent(resolvedAgent ?? undefined);
		});
	}, [getAndStoreAgentByExternalConfigReference]);

	return agent;
};
